import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContext } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import useAppTranslation from "../hooks/useAppTranslation";
import { DATE_LOCALE_BY_LANG } from "../i18n/constants";
import { USER_ROLE } from "../service/specialistService";
import {
  getCabinetNotifications,
  markCabinetNotificationAsRead,
  parseCabinetNotificationsList,
  normalizeCabinetNotification,
  countUnreadNotifications,
} from "../service/notificationService";

export const NotificationContext = createContext({
  unreadCount: 0,
  notifications: [],
  loading: false,
  error: null,
  refreshNotifications: async () => {},
  markAsRead: async () => {},
  setUnreadCount: () => {},
});

const POLL_MS = 60000;

export function NotificationProvider({ children }) {
  const { t } = useAppTranslation();
  const { language } = useLanguage();
  const { isAuthenticated, loading: authLoading, userRole } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dateLocale = DATE_LOCALE_BY_LANG[language] || DATE_LOCALE_BY_LANG.az;
  const defaultTitle = t("profilePage.notifications.defaultTitle");
  const loadErrorText = t("profilePage.notifications.loadError");

  const canFetch =
    !authLoading && isAuthenticated && userRole !== USER_ROLE.SPECIALIST;

  const refreshNotifications = useCallback(async () => {
    if (!canFetch) {
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getCabinetNotifications();
      const list = parseCabinetNotificationsList(data);
      const normalized = list
        .map((item) =>
          normalizeCabinetNotification(item, { defaultTitle, dateLocale })
        )
        .filter((item) => item.id != null && item.id !== "");

      setNotifications(normalized);
      setUnreadCount(countUnreadNotifications(normalized));
    } catch (err) {
      if (err?.response?.status !== 401 && err?.response?.status !== 403) {
        console.error("Unread notifications count failed:", err);
      }
      setError(loadErrorText);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [canFetch, defaultTitle, dateLocale, loadErrorText]);

  const markAsRead = useCallback(async (id) => {
    await markCabinetNotificationAsRead(id);
    setNotifications((prev) => {
      const next = prev.map((item) =>
        item.id === id ? { ...item, isRead: true } : item
      );
      setUnreadCount(countUnreadNotifications(next));
      return next;
    });
  }, []);

  useEffect(() => {
    if (!canFetch) {
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      setLoading(false);
      return undefined;
    }

    refreshNotifications();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshNotifications();
      }
    }, POLL_MS);

    const onFocus = () => refreshNotifications();
    window.addEventListener("focus", onFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [canFetch, refreshNotifications]);

  const value = useMemo(
    () => ({
      unreadCount,
      notifications,
      loading,
      error,
      refreshNotifications,
      markAsRead,
      setUnreadCount,
    }),
    [unreadCount, notifications, loading, error, refreshNotifications, markAsRead]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}

export default NotificationProvider;
